import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AppConsentService } from './app-consent.service';

/**
 * Determines whether a resolved tracking ID is a real value (not an unreplaced
 * `__PLACEHOLDER__` token or an all-X dev placeholder like `G-XXXXXXXXXX`).
 */
export function isValidGa4Id(value: string | undefined): value is string {
  if (!value) return false;
  if (value.startsWith('__') && value.endsWith('__')) return false;
  if (/^[GX\-]+$/.test(value)) return false;
  return true;
}

/**
 * Pure, framework-free resolver mapping a router URL to a GA4 measurement ID.
 *
 * Routing contract (main SSR app):
 * - `/registration/landing`      → GA4_MEASUREMENT_ID_LANDING
 * - `/registration/celcomdigi`   → GA4_MEASUREMENT_ID_CELCOMDIGI
 * - `/registration/maxis`        → GA4_MEASUREMENT_ID_MAXIS
 * - `/registration` (+ subpaths) → GA4_MEASUREMENT_ID_TTPOI
 * - `/onboarding` (+ subpaths)   → GA4_MEASUREMENT_ID_TTPOI
 * - anything else                → undefined (route is not tracked)
 *
 * More specific paths (landing, celcomdigi, maxis) are matched before the
 * generic `/registration` fallback. Returns `undefined` when the resolved
 * measurement ID is missing or still a placeholder, so an unconfigured
 * environment simply does not load GA4.
 */
export function resolveGa4MeasurementId(url: string): string | undefined {
  const path = (url.split('?')[0].split('#')[0] || '/').toLowerCase().replace(/\/+$/, '') || '/';

  // Landing funnel → dedicated property (isolated from the main site).
  if (path === '/registration/landing' || path.startsWith('/registration/landing/')) {
    return isValidGa4Id(environment.GA4_MEASUREMENT_ID_LANDING)
      ? environment.GA4_MEASUREMENT_ID_LANDING
      : undefined;
  }

  // Tenant-specific registration variants keep their own properties.
  if (path === '/registration/celcomdigi' || path.startsWith('/registration/celcomdigi/')) {
    return isValidGa4Id(environment.GA4_MEASUREMENT_ID_CELCOMDIGI)
      ? environment.GA4_MEASUREMENT_ID_CELCOMDIGI
      : undefined;
  }
  if (path === '/registration/maxis' || path.startsWith('/registration/maxis/')) {
    return isValidGa4Id(environment.GA4_MEASUREMENT_ID_MAXIS)
      ? environment.GA4_MEASUREMENT_ID_MAXIS
      : undefined;
  }

  // Main registration + onboarding → TTPOI property.
  if (
    path === '/registration' ||
    path.startsWith('/registration/') ||
    path === '/onboarding' ||
    path.startsWith('/onboarding/')
  ) {
    return isValidGa4Id(environment.GA4_MEASUREMENT_ID_TTPOI)
      ? environment.GA4_MEASUREMENT_ID_TTPOI
      : undefined;
  }

  return undefined;
}

/**
 * Router-driven GA4 tracking for the main SSR application.
 *
 * Distinct from the embed `TrackingService` (which is CustomEvent/element driven
 * for the Shadow-DOM web component). This service:
 * - Runs **browser-only** — a no-op during SSR (guarded by PLATFORM_ID).
 * - Loads `gtag.js` lazily on the first tracked navigation.
 * - Fires a GA4 `page_view` on every `NavigationEnd`, routing it to the correct
 *   property via `send_to` (see {@link resolveGa4MeasurementId}).
 * - Supports multiple GA4 properties from a single `gtag.js` load by calling
 *   `config` once per distinct measurement ID.
 * - Never throws into the app — all tag interaction is wrapped in try/catch.
 *
 * ### Consent gating (Privacy Notice)
 * The main first-party TTPOI site has no separate cookie banner — the consent
 * gate is the **Privacy Notice ("Notice") acceptance** in the agreement pop-up,
 * surfaced via {@link AppConsentService}.
 *
 * ### Consent model (GA4 Consent Mode v2)
 * - `page_view` fires for **all** visitors on every tracked navigation, so raw
 *   traffic/visitor volume is always measured.
 * - Storage consent defaults to **denied**, so pre-consent `page_view` hits are
 *   sent as **cookieless pings** (no `_ga` cookie, no client identifier) — this
 *   keeps traffic counting compliant without a cookie banner.
 * - When the user accepts the Privacy Notice, {@link AppConsentService} flips to
 *   `granted`; we call `gtag('consent','update',{analytics_storage:'granted'})`
 *   to upgrade to full (cookie-based) measurement.
 * - The **conversion** event (`generate_lead`) is mandated on consent — it only
 *   fires after the Privacy Notice has been accepted (see {@link trackConversion}).
 */
@Injectable({ providedIn: 'root' })
export class AppTrackingService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly consent = inject(AppConsentService);

  /** Recorded indications for tracking failures (non-fatal, for logging/tests). */
  readonly indications: string[] = [];

  private initialised = false;
  private gtagScriptRequested = false;
  private consentModeReady = false;
  private readonly configuredIds = new Set<string>();

  /**
   * Begin tracking route changes. Idempotent and browser-only.
   * Call once from the root component.
   */
  init(): void {
    if (this.initialised) return;
    if (!isPlatformBrowser(this.platformId)) return; // SSR: no-op
    this.initialised = true;

    // On consent grant, upgrade GA4 Consent Mode to full (cookie-based) measurement.
    this.consent.state$.subscribe((state) => {
      if (state === 'granted') {
        this.updateConsentMode('granted');
      }
    });

    // page_view fires for ALL traffic (cookieless until consent).
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.sendPageView(e.urlAfterRedirects));
  }

  /**
   * Fire the conversion event (`generate_lead`) for a verified submission.
   *
   * Mandated on consent: only dispatched once the Privacy Notice has been
   * accepted (AppConsentService === 'granted'). Routes to the GA4 property of
   * the current route. Safe to call from SSR/embed contexts (no-ops).
   */
  trackConversion(params: Record<string, unknown> = {}): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.consent.state !== 'granted') {
      this.recordIndication('Conversion suppressed — Privacy Notice consent not granted');
      return;
    }

    try {
      const url = this.router.url;
      const id = resolveGa4MeasurementId(url);
      if (!id) return;

      this.ensureConfigured(id);

      const w = window as any;
      if (!w.gtag) return;

      w.gtag('event', 'generate_lead', {
        send_to: id,
        ...params,
      });
    } catch (err) {
      this.recordIndication('GA4 conversion event failed', err);
    }
  }

  /** Dispatch a GA4 page_view to the resolved property (all traffic). */
  private sendPageView(url: string): void {
    try {
      const id = resolveGa4MeasurementId(url);
      if (!id) return; // untracked route (e.g. /health, /recaptcha)

      this.ensureConfigured(id);

      const w = window as any;
      if (!w.gtag) return;

      w.gtag('event', 'page_view', {
        send_to: id,
        page_path: url,
        page_location: this.document.location?.href,
        page_title: this.document.title,
      });
    } catch (err) {
      this.recordIndication('GA4 page_view failed', err);
    }
  }

  /**
   * Ensure gtag.js is loaded (once), Consent Mode defaults are set (once), and the
   * given measurement ID is configured (once). A single gtag.js load serves all
   * properties; each ID is configured with `send_page_view: false` so we control
   * page_view dispatch explicitly.
   */
  private ensureConfigured(measurementId: string): void {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    if (!w.gtag) {
      w.gtag = function () {
        // eslint-disable-next-line prefer-rest-params
        w.dataLayer.push(arguments);
      };
      w.gtag('js', new Date());
    }

    // GA4 Consent Mode v2 defaults — set BEFORE any config so pre-consent hits
    // are cookieless. If the user already consented earlier in the session
    // (AppConsentService rehydrated 'granted'), start in the granted state.
    if (!this.consentModeReady) {
      this.consentModeReady = true;
      const initialAnalytics = this.consent.state === 'granted' ? 'granted' : 'denied';
      w.gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: initialAnalytics,
      });
    }

    if (!this.gtagScriptRequested) {
      this.gtagScriptRequested = true;
      const script = this.document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.onerror = () => {
        this.recordIndication('GA4 script failed to load (likely blocked by ad blocker)');
      };
      this.document.head.appendChild(script);
    }

    if (!this.configuredIds.has(measurementId)) {
      this.configuredIds.add(measurementId);
      w.gtag('config', measurementId, { send_page_view: false });
    }
  }

  /** Update GA4 Consent Mode storage consent (called on Privacy Notice acceptance). */
  private updateConsentMode(analyticsStorage: 'granted' | 'denied'): void {
    try {
      const w = window as any;
      if (!w.gtag) return; // gtag not loaded yet; default applies on first config
      w.gtag('consent', 'update', { analytics_storage: analyticsStorage });
    } catch (err) {
      this.recordIndication('GA4 consent update failed', err);
    }
  }

  private recordIndication(message: string, error?: unknown): void {
    const indication = error
      ? `${message}: ${error instanceof Error ? error.message : String(error)}`
      : message;
    this.indications.push(indication);
    console.warn('[AppTrackingService]', indication);
  }
}
